import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../tokens/types';
import type { Severity } from '../tokens/designTokens';
import { PdfSection, PdfChip } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenChip } from '../primitives/ScreenPrimitives';
import { colors, fontFamily, severity as SEV_TOKENS, space, typeScale } from '../tokens/designTokens';
import { getParameterImportance, IMPORTANCE_META, type ParameterImportance } from '../tokens/riskFramework';

type Cell = {
    name: string;
    severity: Severity | 'NONE';
    score: number | null;
    tooltip?: string;
    importance: ParameterImportance | null;
};
type CategoryBlock = { title: string; headerScore: number; rollup: Severity | null; cells: Cell[] };

type Scale = 'high-better' | 'low-better';

const detectScale = (data: ReportData): Scale => {
    const headers = data.score?.headers ?? [];
    let max = 0;
    headers.forEach((h) => {
        h.metrics.forEach((m) => {
            if (m.metricScore != null && m.metricScore > max) max = m.metricScore;
        });
    });
    return max > 5 ? 'high-better' : 'low-better';
};

const metricToSeverity = (score: number | null | undefined, tooltip: string | null | undefined, scale: Scale): Cell['severity'] => {
    if (score == null) {
        if (tooltip) {
            const t = tooltip.toLowerCase();
            if (t.includes('severe')) return 'SEVERE';
            if (t.includes('high')) return 'HIGH';
            if (t.includes('medium')) return 'MEDIUM';
            if (t.includes('low')) return 'LOW';
        }
        return 'NONE';
    }
    if (scale === 'high-better') {
        // 0–4 scale. Cutoffs at integer thirds.
        if (score >= 3) return 'LOW';
        if (score >= 2) return 'MEDIUM';
        if (score >= 1) return 'HIGH';
        return 'SEVERE';
    }
    // Proposed framework (1–4, higher is worse risk): 1=LOW, 2=MEDIUM, 3=HIGH, 4=SEVERE.
    // Half-step boundaries round fractional aggregates to the nearest integer bucket.
    if (score >= 3.5) return 'SEVERE';
    if (score >= 2.5) return 'HIGH';
    if (score >= 1.5) return 'MEDIUM';
    return 'LOW';
};

const rollup = (cells: Cell[]): Severity | null => {
    const weights: Record<Severity, number> = { SEVERE: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
    const scored = cells.filter((c) => c.severity !== 'NONE');
    if (scored.length === 0) return null;
    let worst: Severity = scored[0].severity as Severity;
    scored.forEach((c) => {
        if (weights[c.severity as Severity] > weights[worst]) worst = c.severity as Severity;
    });
    return worst;
};

const buildBlocks = (data: ReportData): CategoryBlock[] | null => {
    const headers = data.score?.headers;
    if (!headers || headers.length === 0) return null;
    const scale = detectScale(data);
    const blocks = headers
        .filter((h) => h.metrics && h.metrics.length > 0)
        .map((h) => {
            const cells: Cell[] = h.metrics.map((m) => {
                const importance = getParameterImportance(m.displayName);
                const baseTip = m.tooltipDescription ?? undefined;
                const tooltip = importance
                    ? `Importance: ${IMPORTANCE_META[importance].label} (${IMPORTANCE_META[importance].weight}%)${
                          baseTip ? ` — ${baseTip}` : ''
                      }`
                    : baseTip;
                return {
                    name: m.displayName,
                    score: m.metricScore ?? null,
                    severity: metricToSeverity(m.metricScore, m.tooltipDescription, scale),
                    tooltip,
                    importance,
                };
            });
            return { title: h.displayName, headerScore: h.headerScore, rollup: rollup(cells), cells };
        });
    return blocks.length > 0 ? blocks : null;
};

const isSingletonCategory = (b: CategoryBlock) => b.cells.length === 1 && b.cells[0].name === b.title;

const sevColor = (s: Cell['severity']) => {
    if (s === 'NONE') return { bg: colors.surfaceAlt, fg: colors.mutedSoft, dot: colors.mutedSoft };
    const t = SEV_TOKENS[s];
    return { bg: t.bg, fg: t.fg, dot: t.fg };
};

const LEGEND: Array<{ label: string; state: Cell['severity'] }> = [
    { label: 'No data', state: 'NONE' },
    { label: 'Low risk', state: 'LOW' },
    { label: 'Medium', state: 'MEDIUM' },
    { label: 'High', state: 'HIGH' },
    { label: 'Severe', state: 'SEVERE' },
];

// Letter pip in the corner of each cell. Renders importance from the framework
// (C / H / M / L) so a SEVERE rating on a Critical parameter reads differently
// from the same rating on a Low one. The mark is intentionally subtle; users
// who care about importance will read it, others see it as a quiet tag.
const importanceTooltip = (imp: ParameterImportance): string =>
    `Importance: ${IMPORTANCE_META[imp].label} (${IMPORTANCE_META[imp].weight}%)`;

const COLS = 5;

const padToGrid = (cells: Cell[]): Array<Cell | null> => {
    const remainder = cells.length % COLS;
    if (remainder === 0) return cells;
    const empties: null[] = Array(COLS - remainder).fill(null);
    return [...cells, ...empties];
};

/* ──────────────────────── PDF ──────────────────────── */

const PdfLegend: React.FC = () => (
    <View
        style={{
            flexDirection: 'row',
            gap: 10,
            flexWrap: 'wrap',
            marginBottom: space.sp3,
            paddingBottom: space.sp2,
            borderBottomWidth: 0.5,
            borderBottomColor: colors.rule,
        }}
    >
        {LEGEND.map((l) => {
            const c = sevColor(l.state);
            return (
                <View key={l.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                    <View
                        style={{
                            width: 7,
                            height: 7,
                            backgroundColor: l.state === 'NONE' ? colors.neutralSoft : c.bg,
                            borderRadius: 1,
                            ...(l.state === 'NONE' ? { borderWidth: 0.4, borderColor: colors.rule } : {}),
                        }}
                    />
                    <Text style={{ fontSize: typeScale.micro, color: colors.muted }}>{l.label}</Text>
                </View>
            );
        })}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, marginLeft: 4 }}>
            <Text style={{ fontSize: typeScale.micro, color: colors.muted, fontFamily: fontFamily.sansBold, letterSpacing: 0.3 }}>
                C / H / M / L
            </Text>
            <Text style={{ fontSize: typeScale.micro, color: colors.muted }}>= parameter importance (Critical · High · Medium · Low)</Text>
        </View>
    </View>
);

const PdfCompactCell: React.FC<{ cell: Cell | null; wide?: boolean }> = ({ cell, wide }) => {
    const isEmpty = !cell;
    const c = isEmpty ? sevColor('NONE') : sevColor(cell.severity);
    return (
        <View
            style={{
                width: wide ? '30%' : '19%',
                flexGrow: 1,
                minHeight: wide ? 48 : 40,
                backgroundColor: c.bg,
                borderRadius: 2,
                padding: wide ? 6 : 4,
                paddingTop: wide ? 8 : 6,
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isEmpty ? 0.35 : 1,
                position: 'relative',
            }}
            wrap={false}
        >
            {cell?.importance ? (
                <View
                    style={{
                        position: 'absolute',
                        top: 2,
                        right: 3,
                        paddingHorizontal: 2.5,
                        paddingTop: 1,
                        paddingBottom: 2,
                        backgroundColor: c.fg,
                        borderRadius: 1.5,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Text
                        style={{
                            fontSize: 5.5,
                            color: c.bg,
                            fontFamily: fontFamily.sansBold,
                            letterSpacing: 0.2,
                            lineHeight: 1,
                        }}
                    >
                        {IMPORTANCE_META[cell.importance].abbr}
                    </Text>
                </View>
            ) : null}
            <View style={{ width: wide ? 5 : 4, height: wide ? 5 : 4, borderRadius: 3, backgroundColor: c.dot, marginBottom: 3 }} />
            {cell && (
                <Text
                    style={{
                        fontSize: wide ? typeScale.bodySm : typeScale.micro,
                        color: colors.ink10,
                        textAlign: 'center',
                        lineHeight: 1.3,
                        fontFamily: fontFamily.body,
                    }}
                >
                    {cell.name}
                </Text>
            )}
            {cell && wide && cell.severity !== 'NONE' && (
                <Text
                    style={{
                        fontSize: typeScale.micro,
                        color: c.fg,
                        marginTop: 2,
                        fontFamily: fontFamily.sansBold,
                        textTransform: 'uppercase',
                        letterSpacing: 0.3,
                    }}
                >
                    {SEV_TOKENS[cell.severity].label}
                </Text>
            )}
            {cell?.importance && wide ? (
                <Text style={{ fontSize: typeScale.micro, color: colors.ink40, marginTop: 1, letterSpacing: 0.3 }}>
                    {IMPORTANCE_META[cell.importance].label} importance
                </Text>
            ) : null}
        </View>
    );
};

const PdfCategoryBlock: React.FC<{ block: CategoryBlock }> = ({ block }) => {
    const gridCells = padToGrid(block.cells);
    return (
        <View style={{ marginBottom: space.sp3 }} minPresenceAhead={60}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ fontSize: typeScale.bodySm, fontFamily: fontFamily.sansBold, color: colors.ink }}>{block.title}</Text>
                {block.rollup ? (
                    <PdfChip tone={block.rollup}>{SEV_TOKENS[block.rollup].label}</PdfChip>
                ) : (
                    <PdfChip tone="muted">No data</PdfChip>
                )}
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                {gridCells.map((c, j) => (
                    <PdfCompactCell key={j} cell={c} />
                ))}
            </View>
        </View>
    );
};

const COLS_WIDE = 3;
const padToWideGrid = (cells: Cell[]): Array<Cell | null> => {
    const remainder = cells.length % COLS_WIDE;
    if (remainder === 0) return cells;
    return [...cells, ...Array(COLS_WIDE - remainder).fill(null)];
};

const PdfRiskParameters: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const blocks = buildBlocks(data);
    if (!blocks) return null;
    const totalCells = blocks.reduce((n, b) => n + b.cells.length, 0);
    const isSparse = blocks.every(isSingletonCategory) && totalCells <= 8;
    return (
        <PdfSection
            kicker="Section · Risk"
            title="Risk parameters"
            subtitle={`Assessment across ${blocks.length} analytical categories and ${totalCells} parameters.`}
            index={index}
            breakBefore={breakBefore}
        >
            <PdfLegend />
            {isSparse ? (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                    {padToWideGrid(blocks.map((b) => b.cells[0])).map((c, i) => (
                        <PdfCompactCell key={i} cell={c} wide />
                    ))}
                </View>
            ) : blocks.every(isSingletonCategory) ? (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                    {padToGrid(blocks.map((b) => b.cells[0])).map((c, i) => (
                        <PdfCompactCell key={i} cell={c} />
                    ))}
                </View>
            ) : (
                blocks.map((b, i) => <PdfCategoryBlock key={i} block={b} />)
            )}
        </PdfSection>
    );
};

/* ──────────────────────── SCREEN ──────────────────────── */

const ScreenLegend: React.FC = () => (
    <div
        style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${colors.rule}` }}
    >
        {LEGEND.map((l) => {
            const c = sevColor(l.state);
            return (
                <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span
                        style={{
                            width: 10,
                            height: 10,
                            background: l.state === 'NONE' ? colors.neutralSoft : c.bg,
                            display: 'inline-block',
                            borderRadius: 2,
                            border: l.state === 'NONE' ? `0.5px solid ${colors.rule}` : undefined,
                        }}
                    />
                    <span style={{ fontSize: 11, color: colors.muted }}>{l.label}</span>
                </span>
            );
        })}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 6 }}>
            <span style={{ fontSize: 10, color: colors.muted, fontWeight: 700, letterSpacing: 0.3 }}>C / H / M / L</span>
            <span style={{ fontSize: 11, color: colors.muted }}>= parameter importance (Critical · High · Medium · Low)</span>
        </span>
    </div>
);

const ScreenCompactCell: React.FC<{ cell: Cell | null; wide?: boolean }> = ({ cell, wide }) => {
    const isEmpty = !cell;
    const c = isEmpty ? sevColor('NONE') : sevColor(cell.severity);
    return (
        <div
            title={cell?.tooltip}
            style={{
                minHeight: wide ? 64 : 48,
                background: c.bg,
                borderRadius: wide ? 6 : 4,
                padding: wide ? '12px 10px' : '8px 6px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: wide ? 4 : 3,
                opacity: isEmpty ? 0.35 : 1,
                position: 'relative',
            }}
        >
            {cell?.importance ? (
                <span
                    title={importanceTooltip(cell.importance)}
                    style={{
                        position: 'absolute',
                        top: 4,
                        right: 5,
                        background: c.fg,
                        color: c.bg,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        padding: '1px 4px',
                        borderRadius: 2,
                        lineHeight: 1,
                    }}
                >
                    {IMPORTANCE_META[cell.importance].abbr}
                </span>
            ) : null}
            <span style={{ width: wide ? 7 : 5, height: wide ? 7 : 5, borderRadius: '50%', background: c.dot, display: 'block' }} />
            {cell && (
                <span
                    style={{
                        fontSize: wide ? 12.5 : 11,
                        color: colors.ink10,
                        textAlign: 'center',
                        lineHeight: 1.3,
                        fontWeight: wide ? 600 : 400,
                    }}
                >
                    {cell.name}
                </span>
            )}
            {cell && wide && cell.severity !== 'NONE' && (
                <span style={{ fontSize: 10, color: c.fg, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {SEV_TOKENS[cell.severity].label}
                </span>
            )}
            {cell?.importance && wide ? (
                <span style={{ fontSize: 10, color: colors.ink40, letterSpacing: 0.3 }}>
                    {IMPORTANCE_META[cell.importance].label} importance
                </span>
            ) : null}
        </div>
    );
};

const ScreenCategoryBlock: React.FC<{ block: CategoryBlock }> = ({ block }) => {
    const gridCells = padToGrid(block.cells);
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: colors.ink }}>{block.title}</span>
                {block.rollup ? (
                    <ScreenChip tone={block.rollup}>{SEV_TOKENS[block.rollup].label}</ScreenChip>
                ) : (
                    <ScreenChip tone="muted">No data</ScreenChip>
                )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                {gridCells.map((c, j) => (
                    <ScreenCompactCell key={j} cell={c} />
                ))}
            </div>
        </div>
    );
};

const ScreenRiskParameters: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const blocks = buildBlocks(data);
    if (!blocks) return null;
    const totalCells = blocks.reduce((n, b) => n + b.cells.length, 0);
    const isSparse = blocks.every(isSingletonCategory) && totalCells <= 8;
    return (
        <ScreenSection
            kicker="Section · Risk"
            title="Risk parameters"
            subtitle={`Assessment across ${blocks.length} analytical categories and ${totalCells} parameters.`}
            index={index}
        >
            <ScreenLegend />
            {isSparse ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    {padToWideGrid(blocks.map((b) => b.cells[0])).map((c, i) => (
                        <ScreenCompactCell key={i} cell={c} wide />
                    ))}
                </div>
            ) : blocks.every(isSingletonCategory) ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                    {padToGrid(blocks.map((b) => b.cells[0])).map((c, i) => (
                        <ScreenCompactCell key={i} cell={c} />
                    ))}
                </div>
            ) : (
                blocks.map((b, i) => <ScreenCategoryBlock key={i} block={b} />)
            )}
        </ScreenSection>
    );
};

export const riskParametersModule: ModuleDefinition = {
    meta: {
        id: 'riskParameters' as any,
        title: 'Risk parameters',
        description: 'Heat-grid of all metrics under each AICA scoring category.',
        dataSource: 'polus/score/fetch',
    },
    hasData: (d) => !!buildBlocks(d),
    Pdf: PdfRiskParameters,
    Screen: ScreenRiskParameters,
};
